import * as Sentry from '@sentry/browser';
import { Lazy } from '@studio/js-utils/async';
import type { Message } from '@studio/js-utils/logger';
import { toJson } from '@tryiris-ai/js-types';
import type { T_Any } from '@tryiris-ai/js-types';
import { pick } from 'lodash-es';
import type { Context, SentryProxy } from './types.js';
import { scrubUrlCredentialsDeep } from '../url-credentials.js';
import { config } from '#~/config/index.js';

/** Lazily-loaded sentry proxy. */
const __sentry = new Lazy((): SentryProxy => {
  // Initialize Sentry.
  if (config.isProduction) {
    Sentry.init({
      // Sentry will mutated the config... clone it first.
      ...structuredClone(config.sentry),
      // Every event, breadcrumbs attached, leaves through here: mask URL logins
      // (a failed camera add echoes rtsp://user:pass@host).
      beforeSend: (event) => scrubUrlCredentialsDeep(event)
    });
  }

  return {
    logEvent: (message: Message, context: Context = {}): void => {
      if (!config.isProduction) {
        return;
      }

      Sentry.withScope((scope) => {
        const { user } = context;

        if (user) {
          scope.setUser(pick(user, 'id', 'email'));
        }

        if (message instanceof Error) {
          Sentry.captureException(message);
        } else {
          Sentry.captureMessage(typeof message === 'string' ? message : toJson(message));
        }
      });
    }
  };
});

/** Sentry proxy. */
export const sentry = new Proxy<SentryProxy>(<T_Any>{}, {
  get(...args): unknown {
    return Reflect.get(__sentry.value, args[1], args[2]);
  }
});
